import { Kicker } from '@developerz/ui';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminOverview } from '../lib/admin';
import {
  type Comparison,
  chartSeries,
  compareTo,
  dayLabel,
  type SeriesPoint,
  shiftDay,
  trendTiers,
  utcToday,
  WEEK,
} from '../lib/radar-trends';
import { AdminTrendCard } from './AdminTrendCard';
import { AdminTrendPanel } from './AdminTrendPanel';
import { tierLabel } from './BillingPlanCard';

// The platform radar's Accounts, Users and Runs bands as trend cards (#6715).
// Presenter: the `GET /v1/admin/overview` body in, nothing fetched. Each card's
// value is the LIVE count from the body; its history is `trends[<metric>]`
// (`lib/radar-trends.ts` holds every rule). One card is selected radar-wide,
// Accounts Total by default, and its band opens the 30-day chart under its grid.
//
// "Today" is ONE memo, re-read whenever the overview changes (a refetch), so the
// caption's comparison date and every card's series advance together across UTC
// midnight; a clock read in the caption alone kept yesterday's date beside today's
// numbers. `now` is injectable so a test can pin it; the app passes nothing.

/** One card: its `trends` key, its live value, and how to read a rise. */
type Metric = {
  readonly key: string;
  readonly label: string;
  readonly live: number;
  /** A rise is bad (Waitlisted). */
  readonly inverse?: boolean;
  /** A rate over a trailing window, drawn as a line rather than steps (Runs last 24h). */
  readonly flow?: boolean;
};

type Card = Metric & {
  readonly title: string;
  readonly series: readonly SeriesPoint[];
  readonly week: Comparison;
};

const DEFAULT_SELECTED = 'accounts.total';

function metricBands(o: AdminOverview): readonly { title: string; metrics: Metric[] }[] {
  const total = t('admin.stat_total');
  return [
    {
      title: t('admin.band_accounts'),
      metrics: [
        { key: 'accounts.total', label: total, live: o.accounts.total },
        ...trendTiers(o.accounts.by_tier, o.trends).map(({ tier, live }) => ({
          key: `accounts.tier.${tier}`,
          label: tierLabel(tier),
          live,
        })),
      ],
    },
    {
      title: t('admin.band_users'),
      metrics: [
        { key: 'users.total', label: total, live: o.users.total },
        { key: 'users.approved', label: t('admin.stat_approved'), live: o.users.approved },
        {
          key: 'users.waitlisted',
          label: t('admin.stat_waitlisted'),
          live: o.users.waitlisted,
          inverse: true,
        },
      ],
    },
    {
      title: t('admin.band_runs'),
      metrics: [
        { key: 'runs.total', label: total, live: o.runs.total },
        {
          key: 'runs.last_24h',
          label: t('admin.stat_last_24h'),
          live: o.runs.last_24h,
          flow: true,
        },
      ],
    },
  ];
}

export function AdminTrendBands(props: { overview: AdminOverview; now?: number }): JSX.Element {
  const today = createMemo(() => {
    void props.overview;
    return utcToday(props.now ?? Date.now());
  });
  const [selected, setSelected] = createSignal(DEFAULT_SELECTED);
  const bands = createMemo(() =>
    metricBands(props.overview).map((band) => ({
      title: band.title,
      cards: band.metrics.map((m): Card => {
        const series = chartSeries(props.overview.trends[m.key] ?? [], m.live, today());
        return {
          ...m,
          title: t('admin.trend_panel_title', { band: band.title, label: m.label }),
          series,
          week: compareTo(series, WEEK, m.inverse === true),
        };
      }),
    })),
  );

  return (
    <>
      <p class="admin-trend__caption">
        {t('admin.trend_caption', {
          date: dayLabel(shiftDay(today(), -WEEK), today()),
          days: WEEK,
        })}
      </p>
      <For each={bands()}>
        {(band) => (
          <section class="admin-band">
            <h2>
              <Kicker as="span">{band.title}</Kicker>
            </h2>
            <ul class="admin-trend__grid" aria-label={band.title}>
              <For each={band.cards}>
                {(card) => (
                  <li class="admin-trend__cell">
                    <AdminTrendCard
                      label={card.label}
                      value={card.live}
                      week={card.week}
                      pressed={selected() === card.key}
                      onSelect={() => setSelected(card.key)}
                    />
                  </li>
                )}
              </For>
            </ul>
            <Show when={band.cards.find((c) => c.key === selected())} keyed>
              {(card) => (
                <AdminTrendPanel
                  title={card.title}
                  series={card.series}
                  week={card.week}
                  flow={card.flow === true}
                  inverse={card.inverse === true}
                  today={today()}
                />
              )}
            </Show>
          </section>
        )}
      </For>
    </>
  );
}
